import { forwardRef, type CSSProperties } from 'react';
import { T } from '@territory/tokens';
import { font, radius, space, weight } from '../scale.js';

export interface CountBadgeProps {
  count: number;
  className?: string;
  style?: CSSProperties;
}

/* verbatim wedge, off-scale */
const BADGE = 18;
const OFFSET_TOP = -4;
const OFFSET_RIGHT = -9;

/*
 * Wedge TabBadge. aria-hidden on purpose: the host (IconButton, TabBar) folds the count into its own accessible name;
 * a visible-to-AT badge was overridden by the host label and the count was silently dropped. Not exported publicly.
 */
export const CountBadge = forwardRef<HTMLSpanElement, CountBadgeProps>(function CountBadge({ count, className, style }, ref) {
  if (!(count > 0)) return null;
  return (
    <span
      ref={ref}
      data-rv-count-badge=""
      aria-hidden="true"
      className={className}
      style={{
        position: 'absolute',
        top: OFFSET_TOP,
        right: OFFSET_RIGHT,
        minWidth: BADGE,
        height: BADGE,
        padding: `0 ${space[1]}px`,
        boxSizing: 'border-box',
        borderRadius: radius.pill,
        background: T.red,
        color: T.onRed,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontFamily: T.mono,
        fontSize: font.caption,
        fontWeight: weight.bold,
        lineHeight: 1,
        fontVariantNumeric: 'tabular-nums',
        border: `1.5px solid ${T.bg}`,
        ...style,
      }}
    >
      {count > 9 ? '9+' : count}
    </span>
  );
});

/* Name suffix a host appends so the count reaches assistive tech ("Inbox, 3 new"). */
export const countLabel = (count: number | undefined): string => (count && count > 0 ? `, ${count > 9 ? 'more than 9' : count} new` : '');
